import React, {
  Dispatch,
  ReactElement,
  SetStateAction,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import useWorkspaceAndItemFromRoute from '../../../../../screens/V3/Workspace/useWorkspaceAndItemFromRoute';
import { V3SelectedAppContext } from '../../../../../contextProviders/SplashScreenProviders/UserAndAccountProviders/V3AppsProvider';

export interface SideBarStateContextType {
  aniState: { isOpen: boolean; isPinning: boolean; isHovered: boolean };
  setAniState: Dispatch<
    SetStateAction<{ isOpen: boolean; isPinning: boolean; isHovered: boolean }>
  >;
  appOrWorkspaceId?: string;
}

export const SideBarStateContext = React.createContext<SideBarStateContextType>(
  {
    aniState: { isPinning: false, isOpen: false, isHovered: false },
    setAniState: () => {},
    appOrWorkspaceId: undefined,
  },
);

const SideBarStateProvider = ({
  children,
}: {
  children: ReactElement | ReactElement[];
}) => {
  const { selectedWorkspace } = useWorkspaceAndItemFromRoute();
  const { selectedProduct } = useContext(V3SelectedAppContext);
  const appOrWorkspaceId = useMemo((): string | undefined => {
    return selectedWorkspace?.id || selectedProduct?.id;
  }, [selectedProduct?.id, selectedWorkspace?.id]);

  const persistedIsOpen = useMemo((): boolean | undefined => {
    // If we don’t have an id yet (common in tests/initial render), don’t touch localStorage
    if (!appOrWorkspaceId) return undefined;

    const selectedNavState = localStorage.getItem(
      `v3Sidebar:${appOrWorkspaceId}`,
    );

    return selectedNavState !== 'closed';
  }, [appOrWorkspaceId]);

  const [aniState, setAniState] = useState<{
    isOpen: boolean;
    isPinning: boolean;
    isHovered: boolean;
  }>({
    isOpen: persistedIsOpen === undefined ? true : persistedIsOpen,
    isPinning: false,
    isHovered: false,
  });

  useEffect(() => {
    // If we don’t have an id yet (common in tests/initial render), don’t touch localStorage
    if (persistedIsOpen === undefined) return;

    setAniState((current) => {
      if (current.isOpen === persistedIsOpen) {
        return current;
      }
      return {
        ...current,
        isOpen: persistedIsOpen,
      };
    });
  }, [persistedIsOpen]);

  return (
    <SideBarStateContext.Provider
      value={{
        appOrWorkspaceId,
        aniState,
        setAniState,
      }}
    >
      {children}
    </SideBarStateContext.Provider>
  );
};

export default SideBarStateProvider;
